import React, { useState, useEffect, useRef } from "react";

import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

import { OnboardingSelectorOptions } from "libLegacy/api/onboarding/onboardingModels";
import {
  createMonitoringPlatformCredentials,
  fetchInvertersPlatform,
} from "libLegacy/api/onboarding/onboarding";

import { BlockStandard } from "components/admin/BlockStandard";
import { CustomSelect } from "components/onboarding/customSelect";
import { SolutionsFrame } from "components/solutions/SolutionsFrame";
import { useToken } from "contexts/UserContext";
import { useSetError, useErrorInfo } from "contexts/ErrorContext";
import { CustomCodes } from "contexts/ErrorContextData";

const FRAME_DATA = {
  title: "Monitorar meu sistema solar",
  buttonText: "Continuar",
  imgSrc: "/static/img/solutions/fotovoltaico.png",
  imgAlt: "",
};

export const ProsumerMontioringCredentials = ({ history }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hidePassword, setHidePassword] = useState(true);
  const token = useToken();
  const setGlobalError = useSetError();
  const errorInfo = useErrorInfo();
  const hasFormError = errorInfo?.code === CustomCodes.INCOMPLETE_FORM;

  const [platforms, setPlatforms] =
    useState<Array<OnboardingSelectorOptions>>();
  const [chosenPlatformId, setChosenPlatformId] =
    useState<OnboardingSelectorOptions["id"]>();

  const refEmail = useRef<HTMLInputElement>(null);
  const refPassword = useRef<HTMLInputElement>(null);
  const skipTo = "/onboarding/energy-operator";

  useEffect(() => {
    const loadSelectData = async (token) => {
      try {
        const data = await fetchInvertersPlatform(token);
        setPlatforms(data);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    };
    loadSelectData(token);
  }, [token, setGlobalError]);

  const handleClick = async () => {
    const email = refEmail.current?.value;
    const password = refPassword.current?.value;
    const desiredPlatform = platforms?.find(
      ({ id }) => id === chosenPlatformId
    );

    if (email && password && desiredPlatform) {
      createCredentials({
        email,
        password,
        desiredPlatform,
        id: chosenPlatformId,
      });
    } else {
      setGlobalError(CustomCodes.INCOMPLETE_FORM);
    }
  };

  const createCredentials = async (formData) => {
    setIsLoading(true);
    try {
      await createMonitoringPlatformCredentials(token, formData);
      handleNextNavigation();
    } catch (error) {
      // @ts-expect-error
      setGlobalError(error.response?.data?.error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextNavigation = () => {
    history.push(skipTo, { returnToDashboard: true });
  };

  const toggleVisibility = () => {
    setHidePassword((prev) => !prev);
  };

  const passwordIcon = (
    <InputAdornment position="end">
      <IconButton onClick={toggleVisibility}>
        {hidePassword ? <Visibility /> : <VisibilityOff />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <SolutionsFrame
      {...FRAME_DATA}
      onButtonClick={handleClick}
      isLoading={isLoading}
      skipTo={skipTo}
    >
      <BlockStandard hideDivider>
        <Typography variant="h2">
          Insira seus dados de acesso à plataforma de monitoramento do seu
          inversor
        </Typography>
        <Typography>
          Para podermos exibir os dados de geração de energia do seu sistema
          solar, precisamos dos seus dados de acesso à plataforma de
          monitoramento dos inversores
        </Typography>
      </BlockStandard>

      {platforms && (
        <BlockStandard hideDivider>
          <CustomSelect
            value={chosenPlatformId}
            onChange={({ target: { value } }) => {
              setChosenPlatformId(value);
            }}
            options={platforms}
          />
        </BlockStandard>
      )}

      {chosenPlatformId && (
        <BlockStandard hideDivider>
          <TextField
            required
            inputRef={refEmail}
            label="Seu login"
            variant="outlined"
            error={hasFormError && !refEmail.current?.value}
          />
          <TextField
            required
            inputRef={refPassword}
            InputProps={{ endAdornment: passwordIcon }}
            label="Senha de acesso"
            type={hidePassword ? "password" : "text"}
            variant="outlined"
            error={hasFormError && !refPassword.current?.value}
          />
        </BlockStandard>
      )}
    </SolutionsFrame>
  );
};
